import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Input } from "@/components/ui/input/Input";
import { Label } from "@/components/ui/label/Label";
import ManagementService from "@/lib/services/management/ManagementService";
import { IPIC } from "@/models/management/PIC";
import { IRole } from "@/models/management/Role";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (formData: any) => void;
  pic: IPIC | null;
};

export function EditPICDialog({ isOpen, onClose, onConfirm, pic }: Props) {
  const [roles, setRoles] = useState<IRole[]>([]);
  const [form, setForm] = useState({
    initial: "",
    name: "",
    email: "",
    role_id: ""
  });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await ManagementService.getRoles();
        setRoles(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
      }
    };

    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen]);

  useEffect(() => {
    if (pic && isOpen) {
      setForm({
        initial: pic.initial,
        name: pic.name || "",
        email: pic.email || "",
        role_id: pic.role?.id.toString() || ""
      });
    }
  }, [pic, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(form);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-card border-border font-lregular">
        <DialogHeader>
          <DialogTitle className="font-lsemibold text-foreground">Edit PIC Details</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Initial</Label>
              <Input required value={form.initial} onChange={e => setForm({...form, initial: e.target.value})} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Role</Label>
              <select 
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
                value={form.role_id}
                onChange={e => setForm({...form, role_id: e.target.value})}
                required
              >
                <option value="" disabled>Select Role</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Full Name</Label>
            <Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Email</Label>
            <Input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
          </div>
          <DialogFooter className="pt-4">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="blue">Save Changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}